import { useState } from 'react'
import styled from 'styled-components'
import { Flex, Skeleton, Text } from '@pancakeswap/uikit'
import { motion, AnimatePresence } from 'framer-motion'
import NextLink from 'next/link'
import useTheme from 'hooks/useTheme'
import { formatTokenPrice } from 'utils/formatTokenPrice'
import { palette } from '../colors'

const QUSDT = '0xC276b91c0e8D10260af7b67009d2683bB7776308'
const QOM_SYMBOL = 'QOM'

const Card = styled(motion.div)<{ $bg: string; $border: string }>`
  width: 100%;
  max-width: 420px;
  padding: 20px;
  border-radius: 20px;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
  box-shadow: 0 12px 40px rgba(10, 26, 51, 0.18);
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const InputBox = styled(motion.div)<{ $bg: string }>`
  background: ${({ $bg }) => $bg};
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: box-shadow 0.2s ease;
  &:hover {
    box-shadow: 0 0 0 1.5px rgba(49, 208, 170, 0.35);
  }
`

const TokenChip = styled(motion.div)<{ $card: string; $border: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ $card }) => $card};
  border: 1px solid ${({ $border }) => $border};
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
  cursor: default;
`

const TokenLogo = styled.img<React.ImgHTMLAttributes<HTMLImageElement>>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  object-fit: cover;
`

const ArrowRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: -2px 0;
`

const ArrowCircle = styled(motion.button)<{ $bg: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  padding: 0;
`

const SwapLink = styled(motion.a)`
  display: block;
  width: 100%;
  padding: 14px 0;
  margin-top: 4px;
  border: 0;
  border-radius: 12px;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  font-family: inherit;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
`

const cardVariants = {
  hidden: { opacity: 0, y: 32, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
}

const rowVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
}

const tokenSlide = {
  enter: (dir: number) => ({ opacity: 0, y: dir * 10 }),
  center: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
  exit: (dir: number) => ({ opacity: 0, y: dir * -10, transition: { duration: 0.18 } }),
}

interface TokenRowProps {
  label: string
  labelColor?: string
  amount: string | React.ReactNode
  logoSrc: string
  symbol: string
  card: string
  border: string
  fg: string
  input: string
  animKey: string
  dir: number
  footer?: React.ReactNode
}

const TokenRow: React.FC<TokenRowProps> = ({
  label, labelColor, amount, logoSrc, symbol, card, border, fg, input, animKey, dir, footer,
}) => (
  <InputBox $bg={input}>
    <Text fontSize="11px" bold color={labelColor ?? fg} style={{ letterSpacing: 0.5 }}>
      {label}
    </Text>
    <Flex justifyContent="space-between" alignItems="center" style={{ gap: 12 }}>
      <AnimatePresence mode="wait" custom={dir}>
        <motion.div key={animKey} custom={dir} variants={tokenSlide} initial="enter" animate="center" exit="exit">
          {typeof amount === 'string' ? (
            <Text fontSize="26px" bold color={fg}>{amount}</Text>
          ) : (
            <>{amount}</>
          )}
        </motion.div>
      </AnimatePresence>
      <TokenChip
        $card={card}
        $border={border}
        style={{ color: fg }}
        whileHover={{ scale: 1.06, transition: { duration: 0.18 } }}
        whileTap={{ scale: 0.96 }}
      >
        <TokenLogo src={logoSrc} alt={symbol} />
        {symbol}
      </TokenChip>
    </Flex>
    {footer}
  </InputBox>
)

interface SwapMockCardProps {
  price?: number
  loading?: boolean
}

const SwapMockCard: React.FC<SwapMockCardProps> = ({ price, loading }) => {
  const { theme } = useTheme()
  const p = palette(theme.isDark)
  const [flipped, setFlipped] = useState(false)
  const [rotation, setRotation] = useState(0)

  const qomAmount = 1
  const qusdtAmount = price ? qomAmount * price : undefined

  const fromSymbol = flipped ? 'qUSDT' : 'QOM'
  const toSymbol = flipped ? 'QOM' : 'qUSDT'
  const fromLogo = flipped
    ? `/images/766/tokens/${QUSDT}.png`
    : `/images/766/tokens/0xa26dfBF98Dd1A32FAe56A3D2B2D60A8a41b0bDF0.png`
  const toLogo = flipped
    ? `/images/766/tokens/0xa26dfBF98Dd1A32FAe56A3D2B2D60A8a41b0bDF0.png`
    : `/images/766/tokens/${QUSDT}.png`

  const fromAmount = flipped
    ? qusdtAmount !== undefined
      ? formatTokenPrice(qusdtAmount)
      : '—'
    : qomAmount.toFixed(2)

  const toAmount = flipped
    ? qomAmount.toFixed(2)
    : qusdtAmount !== undefined
    ? formatTokenPrice(qusdtAmount)
    : null

  const swapHref = flipped
    ? `/swap?inputCurrency=${QUSDT}&outputCurrency=${QOM_SYMBOL}`
    : `/swap?inputCurrency=${QOM_SYMBOL}&outputCurrency=${QUSDT}`

  const handleFlip = () => {
    setFlipped((f) => !f)
    setRotation((r) => r + 180)
  }

  return (
    <Card
      $bg={p.card}
      $border={p.border}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -4, boxShadow: '0 20px 56px rgba(10, 26, 51, 0.28)', transition: { duration: 0.25 } }}
    >
      <motion.div variants={stagger} initial="hidden" animate="visible">
        <motion.div variants={rowVariants}>
          <Flex justifyContent="space-between" alignItems="center" px="4px" mb="4px">
            <Text bold fontSize="18px" color={p.fg}>
              Swap
            </Text>
            <Text fontSize="11px" color={p.fgSecondary} style={{ fontFamily: '"Geist Mono", monospace', letterSpacing: 0.5 }}>
              QOM · QL1
            </Text>
          </Flex>
        </motion.div>

        <motion.div variants={rowVariants}>
          <TokenRow
            label="From"
            amount={fromAmount}
            logoSrc={fromLogo}
            symbol={fromSymbol}
            card={p.card}
            border={p.border}
            fg={p.fg}
            input={p.input}
            animKey={`from-${flipped}`}
            dir={flipped ? -1 : 1}
          />
        </motion.div>

        <motion.div variants={rowVariants}>
          <ArrowRow>
            <ArrowCircle
              $bg={p.cobalt}
              onClick={handleFlip}
              animate={{ rotate: rotation }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Flip swap direction"
            >
              ↓
            </ArrowCircle>
          </ArrowRow>
        </motion.div>

        <motion.div variants={rowVariants}>
          <TokenRow
            label="To"
            labelColor={p.fgSecondary}
            amount={
              loading || toAmount === null ? (
                <Skeleton width="100px" height="32px" />
              ) : (
                <Text fontSize="26px" bold color={p.fg}>{toAmount}</Text>
              )
            }
            logoSrc={toLogo}
            symbol={toSymbol}
            card={p.card}
            border={p.border}
            fg={p.fg}
            input={p.input}
            animKey={`to-${flipped}`}
            dir={flipped ? 1 : -1}
            footer={
              <AnimatePresence mode="wait">
                <motion.div
                  key={`rate-${flipped}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { delay: 0.1 } }}
                  exit={{ opacity: 0 }}
                >
                  <Text fontSize="11px" color={p.fgSecondary} style={{ fontFamily: '"Geist Mono", monospace' }}>
                    {price
                      ? flipped
                        ? `1 qUSDT ≈ ${formatTokenPrice(1 / price)} QOM`
                        : `1 QOM ≈ ${formatTokenPrice(price)} qUSDT`
                      : ' '}
                  </Text>
                </motion.div>
              </AnimatePresence>
            }
          />
        </motion.div>

        <motion.div variants={rowVariants}>
          <NextLink href={swapHref} passHref legacyBehavior>
            <SwapLink
              style={{ background: p.cobalt }}
              whileHover={{ filter: 'brightness(1.12)', scale: 1.02, transition: { duration: 0.18 } }}
              whileTap={{ scale: 0.97 }}
            >
              Open Swap →
            </SwapLink>
          </NextLink>
        </motion.div>
      </motion.div>
    </Card>
  )
}

export default SwapMockCard
